import { useQuery } from '@tanstack/react-query';
import {
  appointmentsApi,
  type ListPortalClinicsResponse,
  type ListUnlinkedAppointmentRequestsResponse,
  type MyAppointmentsResponse,
  type PortalBookingOptionsResponse,
} from '../infrastructure/appointments.api';

// Query keys for the portal appointments feature. Commands invalidate against `all` so reads refetch after a write.
export const appointmentKeys = {
  all: ['portal-appointments'] as const,
  list: () => ['portal-appointments', 'list'] as const,
  bookingOptions: (providerId: string | null, serviceId: string | null) =>
    ['portal-appointments', 'booking-options', providerId, serviceId] as const,
  clinics: (search: string) => ['portal-appointments', 'clinics', search] as const,
  clinicBookingOptions: (clinicId: string | null, providerId: string | null, serviceId: string | null) =>
    ['portal-appointments', 'clinic-booking-options', clinicId, providerId, serviceId] as const,
  unlinkedRequests: () => ['portal-appointments', 'unlinked-requests'] as const,
};

/** Read — the patient's own appointments, bucketed by the server into pending/upcoming/past. */
export function useMyAppointmentsQuery() {
  return useQuery<MyAppointmentsResponse>({
    queryKey: appointmentKeys.list(),
    queryFn: () => appointmentsApi.list(),
  });
}

export function usePortalBookingOptionsQuery(providerId: string | null, serviceId: string | null) {
  return useQuery<PortalBookingOptionsResponse>({
    queryKey: appointmentKeys.bookingOptions(providerId, serviceId),
    queryFn: () => appointmentsApi.bookingOptions({ providerId, serviceId, days: 14 }),
  });
}

export function usePortalClinicsQuery(search: string) {
  return useQuery<ListPortalClinicsResponse>({
    queryKey: appointmentKeys.clinics(search.trim()),
    queryFn: () => appointmentsApi.listClinics(search),
  });
}

export function useUnlinkedAppointmentRequestsQuery() {
  return useQuery<ListUnlinkedAppointmentRequestsResponse>({
    queryKey: appointmentKeys.unlinkedRequests(),
    queryFn: () => appointmentsApi.listUnlinkedRequests(),
  });
}

export function useClinicBookingOptionsQuery(
  clinicId: string | null,
  providerId: string | null,
  serviceId: string | null
) {
  return useQuery<PortalBookingOptionsResponse>({
    queryKey: appointmentKeys.clinicBookingOptions(clinicId, providerId, serviceId),
    queryFn: () => appointmentsApi.clinicBookingOptions(clinicId!, { providerId, serviceId, days: 14 }),
    enabled: Boolean(clinicId),
  });
}
